import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import RefundProjectModal from './RefundProjectModal';
import {
  FaDiagramProject,
  FaUser,
  FaCalendar,
  FaCircleCheck,
  FaXmark,
  FaEnvelope,
  FaEye,
  FaArrowLeft,
  FaCreditCard,
  FaUserTie,
  FaFileLines,
  FaArrowUpRightFromSquare,
} from 'react-icons/fa6';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface UserId {
  _id: string;
  name: string;
  email: string;
  role: string | null;
}

interface ProjectDocument {
  MVP_Scope: { pdf: string | null };
  ICP_Report: { pdf: string | null };
  ICP_Interview_Guide: { pdf: string | null };
  Value_Propositions_Report: { pdf: string | null };
  Competitor_Snapshot: { pdf: string | null };
  GTM_Strategy: { pdf: string | null };
  investorDeck: {
    pdf: string | null;
    ppt: string | null;
    pptStatus: string;
    pptError: string | null;
  };
  financialProjection: {
    summary: string | null;
    excel: string | null;
    inputs: string | null;
    projections: any[];
    generatedAt: string | null;
  };
}

interface Project {
  _id: string;
  projectName: string;
  description: string;
  userId: UserId;
  status: string;
  projectStatus: string;
  isDeleted: boolean;
  document: ProjectDocument;
  qualityScore: number;
  currentDocumentVersion: number;
  userPersona: any[];
  valueProps: any[];
  features: any[];
  requirements: any[];
  coreBusinessQuestion: any[];
  followUpQuestion: any[];
  ads: {
    meta?: { instagram?: { content: string; imageUrl: string | null } };
    google?: { content: { keywords: string[]; headlines: string[]; descriptions: string[] }; imageUrl: string | null };
    x?: { content: string; imageUrl: string | null };
  } | null;
  founderKitPaid: boolean;
  founderSessionPaid: boolean;
  refundInfo: {
    isRefunded: boolean;
    amount?: number;
    currency?: string;
    refundedAt?: string;
    stripeRefundId?: string;
  };
  paidAdvisorIds: string[];
  sessionBookings: any[];
  willAnswerFollowUpQuestion: boolean;
  lastDocumentUpdated: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const ADVISOR_NAMES: Record<string, string> = {
  mahesh: 'Mahesh',
  de: 'DE',
};

const FRONTEND_BASE_URL = process.env.REACT_APP_LANDING_URL;

const getInitials = (name: string) =>
  (name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

const formatDate = (dateString: string) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const statusColor: Record<string, string> = {
  active: 'bg-emerald-500',
  published: 'bg-emerald-500',
  draft: 'bg-gray-400',
  deleted: 'bg-red-500',
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white capitalize">
    <span className={`w-1.5 h-1.5 rounded-full ${statusColor[status] ?? 'bg-gray-400'}`} />
    {status}
  </span>
);

const Card: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`}
  >
    {children}
  </motion.div>
);

const CardHeader: React.FC<{ title: string; icon: React.ReactNode; accent: string }> = ({ title, icon, accent }) => (
  <div className={`flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-50`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}>
      {icon}
    </div>
    <h2 className="text-sm font-semibold text-gray-800 tracking-wide">{title}</h2>
  </div>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-400 font-medium">{label}</span>
    <span className="text-xs font-semibold text-gray-700">{value}</span>
  </div>
);

const StatusChip: React.FC<{ done: boolean; label: string; count?: number }> = ({ done, label, count }) => (
  <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
    done ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-gray-50 border-gray-100 text-gray-400'
  }`}>
    <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-emerald-500' : 'bg-gray-200'}`}>
      {done
        ? <FaCircleCheck className="w-2.5 h-2.5 text-white" />
        : <FaXmark className="w-2.5 h-2.5 text-gray-400" />}
    </span>
    <span className="flex-1 leading-tight">{label}</span>
    {count !== undefined && count > 0 && (
      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-md ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
        {count}
      </span>
    )}
  </div>
);

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hostedLandingUrl, setHostedLandingUrl] = useState<string>('');
  const [refundModalOpen, setRefundModalOpen] = useState(false);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await api.get(`/admin/projects/${projectId}`);
      return (response.data.data || response.data) as Project;
    },
    enabled: !!projectId,
  });

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    const fetchLandingUrl = async () => {
      try {
        const response = await api.get(`/admin/projects/${projectId}/landing-page`);
        if (!alive) return;
        const responseData = response?.data?.data ?? response?.data;
        const site = Array.isArray(responseData) ? responseData[0] ?? null : responseData;
        const subdomain = site?.subdomain;
        const customDomain = site?.customDomain;
        const url = customDomain
          ? customDomain.startsWith('http') ? customDomain : `https://${customDomain}`
          : subdomain
          ? `https://${subdomain}.${FRONTEND_BASE_URL}`
          : '';
        setHostedLandingUrl(url);
      } catch {
        if (alive) setHostedLandingUrl('');
      }
    };
    fetchLandingUrl();
    return () => { alive = false; };
  }, [projectId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <FaDiagramProject className="w-12 h-12 text-gray-300" />
        <p className="text-gray-500 font-medium">Project not found</p>
        <button onClick={() => navigate('/projects')} className="text-primary text-sm hover:underline">
          Back to Projects
        </button>
      </div>
    );
  }

  const owner = project.userId;
  const userPersonas = Array.isArray(project.userPersona) ? project.userPersona : [];
  const valueProps = Array.isArray(project.valueProps) ? project.valueProps : [];
  const features = Array.isArray(project.features) ? project.features : [];
  const coreQuestions = Array.isArray(project.coreBusinessQuestion) ? project.coreBusinessQuestion : [];
  const followUps = Array.isArray(project.followUpQuestion) ? project.followUpQuestion : [];

  const DOC_STATUS = [
    { label: 'MVP Scope', done: !!project.document?.MVP_Scope?.pdf },
    { label: 'ICP Report', done: !!project.document?.ICP_Report?.pdf },
    { label: 'ICP Interview Guide', done: !!project.document?.ICP_Interview_Guide?.pdf },
    { label: 'Value Propositions', done: !!project.document?.Value_Propositions_Report?.pdf },
    { label: 'Competitor Snapshot', done: !!project.document?.Competitor_Snapshot?.pdf },
    { label: 'GTM Strategy', done: !!project.document?.GTM_Strategy?.pdf },
    { label: 'Pitch Summary', done: !!(project.document?.investorDeck?.ppt || project.document?.investorDeck?.pdf) },
  ];

  const CONTENT_STATUS = [
    { label: 'User Personas', done: userPersonas.length > 0, count: userPersonas.length },
    { label: 'Value Propositions', done: valueProps.length > 0, count: valueProps.length },
    { label: 'Features', done: features.length > 0, count: features.length },
    { label: 'Core Business Questions', done: coreQuestions.length > 0, count: coreQuestions.length },
    { label: 'Follow-up Questions', done: followUps.length > 0, count: followUps.length },
    { label: 'Ad Content', done: !!(project.ads && (project.ads.meta?.instagram || project.ads.x || project.ads.google)) },
    { label: 'Landing Page', done: !!hostedLandingUrl },
  ];

  const docsCompleted = DOC_STATUS.filter(d => d.done).length;
  const projectStatus = project.isDeleted ? 'deleted' : project.status;

  return (
    <>
    <div className="space-y-4 pb-10">

      {/* Back Nav */}
      <button
        onClick={() => navigate('/projects')}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
      >
        <FaArrowLeft className="w-3.5 h-3.5" />
        Back to Projects
      </button>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl shadow-lg bg-gradient-to-br from-[#1D4D87] to-[#1E3A8A]"
      >
        {/* subtle grid pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />

        <div className="relative p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* icon */}
          <div className="w-14 h-14 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center flex-shrink-0">
            <FaDiagramProject className="w-6 h-6 text-white" />
          </div>

          {/* name + badges */}
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-white leading-tight truncate" title={project.projectName}>
              {project.projectName}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <StatusPill status={projectStatus} />

              {project.projectStatus && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 capitalize border border-white/10">
                  {project.projectStatus.replace(/_/g, ' ')}
                </span>
              )}

              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white/70 border border-white/10">
                v{project.currentDocumentVersion}
              </span>

              {hostedLandingUrl && (
                <a
                  href={hostedLandingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-white transition-colors"
                >
                  <FaArrowUpRightFromSquare className="w-2.5 h-2.5" />
                  View Landing Page
                </a>
              )}

              {project.founderKitPaid && !project.isDeleted && !project.refundInfo?.isRefunded && (
                <button
                  onClick={() => setRefundModalOpen(true)}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-500/80 hover:bg-red-500 text-white transition-colors"
                >
                  Refund Project
                </button>
              )}

              {project.refundInfo?.isRefunded && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-500/80 text-white">
                  Refunded {project.refundInfo.refundedAt ? new Date(project.refundInfo.refundedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </span>
              )}
            </div>
          </div>

          {/* stat pills */}
          <div className="flex sm:flex-col gap-3 sm:gap-2 shrink-0">
            {[
              { value: features.length, label: 'Features' },
              { value: userPersonas.length, label: 'Personas' },
              { value: `${docsCompleted}/${DOC_STATUS.length}`, label: 'Docs' },
            ].map(({ value, label }) => (
              <div key={label} className="flex sm:flex-row items-center gap-2 bg-white/10 rounded-xl px-3 py-2 min-w-[80px]">
                <span className="text-lg font-bold text-white leading-none">{value}</span>
                <span className="text-xs text-blue-200 leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Owner + Timeline side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card delay={0.05}>
          <CardHeader title="Project Owner" icon={<FaUser className="w-3.5 h-3.5 text-indigo-600" />} accent="bg-indigo-50" />
          <div className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primaryDark flex items-center justify-center text-white font-bold text-base flex-shrink-0">
              {getInitials(owner?.name || '')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm truncate">{owner?.name || '—'}</p>
              <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                <FaEnvelope className="w-2.5 h-2.5" />
                {owner?.email || '—'}
              </p>
              <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                owner?.role === 'agency_admin' ? 'bg-blue-100 text-blue-700' :
                owner?.role === 'member' ? 'bg-gray-100 text-gray-500' :
                'bg-purple-100 text-purple-700'
              }`}>
                {owner?.role || 'guest'}
              </span>
            </div>
            {owner?._id && (
              <button
                onClick={() => navigate(`/users/${owner._id}`)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primaryDark transition-colors"
              >
                <FaEye className="w-3 h-3" />
                Profile
              </button>
            )}
          </div>
        </Card>

        <Card delay={0.08}>
          <CardHeader title="Timeline" icon={<FaCalendar className="w-3.5 h-3.5 text-purple-600" />} accent="bg-purple-50" />
          <div className="px-5 pb-2 pt-2">
            <InfoRow label="Created" value={formatDate(project.createdAt)} />
            <InfoRow label="Last Updated" value={formatDate(project.updatedAt)} />
            <InfoRow label="Last Document Update" value={formatDate(project.lastDocumentUpdated)} />
            <InfoRow
              label="Deleted"
              value={
                <span className={`font-bold ${project.isDeleted ? 'text-red-500' : 'text-emerald-500'}`}>
                  {project.isDeleted ? 'Yes' : 'No'}
                </span>
              }
            />
          </div>
        </Card>
      </div>

      {/* Payment & Advisor */}
      <Card delay={0.1}>
        <CardHeader title="Payment & Advisors" icon={<FaCreditCard className="w-3.5 h-3.5 text-blue-600" />} accent="bg-blue-50" />
        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Founder Kit */}
          <div className={`rounded-xl p-4 border ${project.founderKitPaid ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-100'}`}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Founder Kit</p>
            <div className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center ${project.founderKitPaid ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                {project.founderKitPaid
                  ? <FaCircleCheck className="w-3 h-3 text-white" />
                  : <FaXmark className="w-3 h-3 text-white" />}
              </span>
              <span className={`text-sm font-semibold ${project.founderKitPaid ? 'text-emerald-700' : 'text-gray-500'}`}>
                {project.founderKitPaid ? 'Paid' : 'Not Paid'}
              </span>
            </div>
          </div>

          {/* Advisor Session */}
          <div className={`rounded-xl p-4 border ${project.founderSessionPaid ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-100'}`}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Advisor Session</p>
            <div className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center ${project.founderSessionPaid ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                {project.founderSessionPaid
                  ? <FaCircleCheck className="w-3 h-3 text-white" />
                  : <FaXmark className="w-3 h-3 text-white" />}
              </span>
              <span className={`text-sm font-semibold ${project.founderSessionPaid ? 'text-emerald-700' : 'text-gray-500'}`}>
                {project.founderSessionPaid ? 'Paid' : 'Not Paid'}
              </span>
            </div>
          </div>

          {/* Paid Advisors */}
          <div className="rounded-xl p-4 border bg-gray-50 border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Paid Advisors</p>
            {(project.paidAdvisorIds ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {(project.paidAdvisorIds ?? []).map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    <FaUserTie className="w-2.5 h-2.5" />
                    {ADVISOR_NAMES[id] || id}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm text-gray-400">None</span>
            )}
          </div>
        </div>

        {(project.sessionBookings ?? []).length > 0 && (
          <div className="px-5 pb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Session Bookings</p>
            <div className="space-y-1.5">
              {(project.sessionBookings ?? []).map((booking: any, i: number) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3.5 py-2.5 text-xs">
                  <span className="font-medium text-gray-700">{booking.advisorId || `Booking ${i + 1}`}</span>
                  <span className="text-gray-400">{formatDate(booking.scheduledAt || booking.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Generated Documents */}
      <Card delay={0.12}>
        <CardHeader title="Generated Documents" icon={<FaFileLines className="w-3.5 h-3.5 text-teal-600" />} accent="bg-teal-50" />
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {DOC_STATUS.map(({ label, done }) => (
            <StatusChip key={label} label={label} done={done} />
          ))}
        </div>
      </Card>

      {/* Project Content */}
      <Card delay={0.15}>
        <CardHeader title="Project Content" icon={<FaDiagramProject className="w-3.5 h-3.5 text-violet-600" />} accent="bg-violet-50" />
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {CONTENT_STATUS.map(({ label, done, count }) => (
            <StatusChip key={label} label={label} done={done} count={count} />
          ))}
        </div>
      </Card>

    </div>

    {project && (
      <RefundProjectModal
        isOpen={refundModalOpen}
        projectId={project._id}
        projectName={project.projectName}
        userEmail={project.userId?.email || ''}
        amountCents={project.refundInfo?.amount ?? 0}
        currency={project.refundInfo?.currency ?? 'usd'}
        onClose={() => setRefundModalOpen(false)}
        onSuccess={() => {
          setRefundModalOpen(false);
          toast.success('Refund processed and project deleted');
          queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        }}
      />
    )}
    </>
  );
};

export default ProjectDetail;
